import { useState } from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PageHeader } from "@/components/app/common";
import { DataTable } from "@/components/app/DataTable";
import { useFetch } from "@/hooks/useFetch";
import { api, errMsg } from "@/lib/api";

function PersonDialog({ person, taken, onClose, onSaved }) {
  const { data } = useFetch(person ? null : "/users");
  const users = (data?.users || []).filter((u) => u.active && u.email && !taken.includes(u.id));
  const [f, setF] = useState(person || { user_id: "", designation: "", phone: "" });
  const pick = (id) => { const u = users.find((x) => x.id === id); setF({ ...f, user_id: id, designation: f.designation || u?.designation || "", phone: f.phone || u?.phone || "" }); };
  const save = async () => {
    try {
      const body = { user_id: f.user_id, designation: f.designation, phone: f.phone };
      person ? await api.put(`/sales-people/${person.id}`, body) : await api.post("/sales-people", body);
      toast.success("Saved"); onSaved(); onClose();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="sales-person-dialog">
        <DialogHeader><DialogTitle>{person ? `Edit ${person.name}` : "Add sales person"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          {!person && <div className="col-span-2"><Label>User *</Label>
            <Select value={f.user_id} onValueChange={pick}>
              <SelectTrigger className="bg-white" data-testid="sales-person-user"><SelectValue placeholder="Pick a user (added under Admin → Users)" /></SelectTrigger>
              <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} · {u.email}</SelectItem>)}</SelectContent>
            </Select></div>}
          <div><Label>Designation (printed on the quote)</Label><Input value={f.designation} onChange={(e) => setF({ ...f, designation: e.target.value })} data-testid="sales-person-designation" /></div>
          <div><Label>Mobile (printed on the quote)</Label><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="sales-person-phone" /></div>
        </div>
        <DialogFooter><Button onClick={save} disabled={!f.user_id} data-testid="sales-person-save-btn">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function SalesTeam() {
  const { data, reload } = useFetch("/sales-people");
  const [edit, setEdit] = useState(null);
  const toggle = async (p, v) => { try { await api.post(`/sales-people/${p.id}/status`, { active: v }); reload(); } catch (e) { toast.error(errMsg(e)); } };
  const cols = [
    { key: "name", label: "Name" }, { key: "designation", label: "Designation" }, { key: "phone", label: "Mobile" }, { key: "email", label: "Email" },
    { key: "active", label: "Can send quotes", render: (p) => <span onClick={(e) => e.stopPropagation()}><Switch checked={p.active} onCheckedChange={(v) => toggle(p, v)} data-testid={`sales-person-active-${p.email}`} /></span> },
  ];
  return (
    <div data-testid="sales-team-page">
      <PageHeader title="Sales Team" subtitle="Only these people can own a quotation and email it to customers. Their name, designation, mobile and email are printed on the quote and its sign-off.">
        <Button onClick={() => setEdit({})} data-testid="sales-person-new-btn"><Plus className="h-4 w-4 mr-1" />Add</Button>
      </PageHeader>
      <DataTable testid="sales-team-table" columns={cols} rows={data} onRowClick={(p) => setEdit(p)} empty="No sales people yet — add the users who send quotations" />
      {edit && <PersonDialog key={edit.id || "new"} person={edit.id ? edit : null} taken={(data || []).map((p) => p.user_id)} onClose={() => setEdit(null)} onSaved={reload} />}
    </div>
  );
}
